/**
 * Customer Onboarding Service
 * 
 * Makes M3.A3 (Enterprise onboarding scales) true.
 * 
 * Evidence criteria:
 * - Batch customer setup is automated
 * - Onboarding progress is trackable
 * - Time-to-value is measurable
 * - Blockers are identifiable
 */

import { getOrCreateSupabaseUser } from './userSync';
import { recordMetric } from './observability';

// ============================================================================
// Types
// ============================================================================

export type OnboardingPhase = 
  | 'contract_signed'
  | 'account_created'
  | 'users_invited'
  | 'data_imported'
  | 'first_cycle_started'
  | 'first_priority_published'
  | 'integration_configured'
  | 'team_trained'
  | 'fully_onboarded';

export interface CustomerOnboarding {
  id: string;
  customerId: string;
  customerName: string;
  
  // Contract info
  contractSignedAt: string;
  contractTier: 'starter' | 'professional' | 'enterprise';
  contractedSeats: number;
  
  // Current state
  currentPhase: OnboardingPhase;
  completedPhases: OnboardingPhase[];
  
  // Progress tracking
  progress: OnboardingProgress;
  
  // Timing
  createdAt: string;
  updatedAt: string;
  targetCompletionDate?: string;
  actualCompletionDate?: string;
  
  // Health
  healthScore: number; // 0-100
  blockers: OnboardingBlocker[];
  
  // Assignment
  csmId?: string; // Customer Success Manager
  csmName?: string;
}

export interface OnboardingProgress {
  usersInvited: number;
  usersActivated: number;
  itemsCreated: number;
  cyclesStarted: number;
  prioritiesPublished: number;
  integrationsConfigured: number;
  trainingSessionsCompleted: number;
}

export interface OnboardingBlocker {
  id: string;
  type: 'technical' | 'adoption' | 'data' | 'training' | 'integration';
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  createdAt: string;
  resolvedAt?: string;
  owner?: string;
}

export interface OnboardingMilestone {
  phase: OnboardingPhase;
  label: string;
  description: string;
  requiredForTier: ('starter' | 'professional' | 'enterprise')[];
  estimatedDays: number;
}

// ============================================================================
// Milestones Definition
// ============================================================================

export const ONBOARDING_MILESTONES: OnboardingMilestone[] = [
  {
    phase: 'contract_signed',
    label: 'Contract Signed',
    description: 'Customer has signed the contract',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 0,
  },
  {
    phase: 'account_created',
    label: 'Account Created',
    description: 'Workspace and admin account are set up',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 1,
  },
  {
    phase: 'users_invited',
    label: 'Users Invited',
    description: 'Initial users have been invited to the workspace',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 3,
  },
  {
    phase: 'data_imported',
    label: 'Data Imported',
    description: 'Initial items/backlog imported from existing tools',
    requiredForTier: ['professional', 'enterprise'],
    estimatedDays: 7,
  },
  {
    phase: 'first_cycle_started',
    label: 'First Cycle Started',
    description: 'Customer has started their first prioritization cycle',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 10,
  },
  {
    phase: 'first_priority_published',
    label: 'First Priority Published',
    description: 'Customer has published their first Published Priority List',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 14,
  },
  {
    phase: 'integration_configured',
    label: 'Integration Configured',
    description: 'At least one external integration is set up',
    requiredForTier: ['professional', 'enterprise'],
    estimatedDays: 14,
  },
  {
    phase: 'team_trained',
    label: 'Team Trained',
    description: 'Core team has completed training',
    requiredForTier: ['enterprise'],
    estimatedDays: 21,
  },
  {
    phase: 'fully_onboarded',
    label: 'Fully Onboarded',
    description: 'Customer is fully onboarded and operational',
    requiredForTier: ['starter', 'professional', 'enterprise'],
    estimatedDays: 30,
  },
];

// ============================================================================
// In-Memory Store
// ============================================================================

const onboardingStore: Map<string, CustomerOnboarding> = new Map();

// ============================================================================
// Onboarding CRUD
// ============================================================================

export async function createOnboarding(
  input: {
    customerId: string;
    customerName: string;
    contractTier: CustomerOnboarding['contractTier'];
    contractedSeats: number;
    csmId?: string;
    csmName?: string;
    targetCompletionDate?: string;
  }
): Promise<CustomerOnboarding> {
  const onboarding: CustomerOnboarding = {
    id: crypto.randomUUID(),
    customerId: input.customerId,
    customerName: input.customerName,
    contractSignedAt: new Date().toISOString(),
    contractTier: input.contractTier,
    contractedSeats: input.contractedSeats,
    currentPhase: 'contract_signed',
    completedPhases: ['contract_signed'],
    progress: {
      usersInvited: 0,
      usersActivated: 0,
      itemsCreated: 0,
      cyclesStarted: 0,
      prioritiesPublished: 0,
      integrationsConfigured: 0,
      trainingSessionsCompleted: 0,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    targetCompletionDate: input.targetCompletionDate,
    healthScore: 100,
    blockers: [],
    csmId: input.csmId,
    csmName: input.csmName,
  };
  
  onboardingStore.set(onboarding.id, onboarding);
  recordMetric('usage', 'customer_onboarding_started', 1, { tier: input.contractTier });
  console.log(`[CustomerOnboarding] Started onboarding for ${input.customerName}`);
  
  return onboarding;
}

export async function getOnboarding(id: string): Promise<CustomerOnboarding | null> {
  return onboardingStore.get(id) || null;
}

export async function getOnboardingByCustomer(customerId: string): Promise<CustomerOnboarding | null> {
  for (const onboarding of onboardingStore.values()) {
    if (onboarding.customerId === customerId) {
      return onboarding;
    }
  }
  return null;
}

export async function listOnboardings(
  filter?: { phase?: OnboardingPhase; csmId?: string; hasBlockers?: boolean }
): Promise<CustomerOnboarding[]> {
  let onboardings = Array.from(onboardingStore.values());
  
  if (filter?.phase) {
    onboardings = onboardings.filter(o => o.currentPhase === filter.phase);
  }
  if (filter?.csmId) {
    onboardings = onboardings.filter(o => o.csmId === filter.csmId);
  }
  if (filter?.hasBlockers !== undefined) {
    onboardings = onboardings.filter(o => 
      filter.hasBlockers 
        ? o.blockers.filter(b => !b.resolvedAt).length > 0
        : o.blockers.filter(b => !b.resolvedAt).length === 0
    );
  }
  
  return onboardings.sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

// ============================================================================
// Phase Progression
// ============================================================================

export async function advancePhase(
  onboardingId: string,
  newPhase: OnboardingPhase
): Promise<CustomerOnboarding | null> {
  const onboarding = await getOnboarding(onboardingId);
  if (!onboarding) return null;
  
  // Add to completed phases if not already there
  if (!onboarding.completedPhases.includes(newPhase)) {
    onboarding.completedPhases.push(newPhase);
  }
  
  onboarding.currentPhase = newPhase;
  onboarding.updatedAt = new Date().toISOString();
  
  // Check if fully onboarded
  if (newPhase === 'fully_onboarded') {
    onboarding.actualCompletionDate = new Date().toISOString();
    recordMetric('usage', 'customer_onboarding_completed', 1, { 
      tier: onboarding.contractTier,
      daysToComplete: calculateDaysToComplete(onboarding),
    });
  }
  
  // Recalculate health score
  onboarding.healthScore = calculateHealthScore(onboarding);
  
  onboardingStore.set(onboardingId, onboarding);
  console.log(`[CustomerOnboarding] ${onboarding.customerName} advanced to ${newPhase}`);
  
  return onboarding;
}

export async function updateProgress(
  onboardingId: string,
  progressUpdate: Partial<OnboardingProgress>
): Promise<CustomerOnboarding | null> {
  const onboarding = await getOnboarding(onboardingId);
  if (!onboarding) return null;
  
  onboarding.progress = {
    ...onboarding.progress,
    ...progressUpdate,
  };
  onboarding.updatedAt = new Date().toISOString();
  onboarding.healthScore = calculateHealthScore(onboarding);
  
  // Auto-advance phases based on progress
  await autoAdvancePhases(onboarding);
  
  onboardingStore.set(onboardingId, onboarding);
  return onboarding;
}

async function autoAdvancePhases(onboarding: CustomerOnboarding): Promise<void> {
  const { progress, completedPhases } = onboarding;
  
  // Check each phase condition
  if (progress.usersInvited > 0 && !completedPhases.includes('users_invited')) {
    await advancePhase(onboarding.id, 'users_invited');
  }
  
  if (progress.itemsCreated > 0 && !completedPhases.includes('data_imported')) {
    await advancePhase(onboarding.id, 'data_imported');
  }
  
  if (progress.cyclesStarted > 0 && !completedPhases.includes('first_cycle_started')) {
    await advancePhase(onboarding.id, 'first_cycle_started');
  }
  
  if (progress.prioritiesPublished > 0 && !completedPhases.includes('first_priority_published')) {
    await advancePhase(onboarding.id, 'first_priority_published');
  }
  
  if (progress.integrationsConfigured > 0 && !completedPhases.includes('integration_configured')) {
    await advancePhase(onboarding.id, 'integration_configured');
  }
  
  if (progress.trainingSessionsCompleted > 0 && !completedPhases.includes('team_trained')) {
    await advancePhase(onboarding.id, 'team_trained');
  }
}

// ============================================================================
// Blockers
// ============================================================================

export async function addBlocker(
  onboardingId: string,
  blocker: Omit<OnboardingBlocker, 'id' | 'createdAt'>
): Promise<OnboardingBlocker | null> {
  const onboarding = await getOnboarding(onboardingId);
  if (!onboarding) return null;
  
  const newBlocker: OnboardingBlocker = {
    ...blocker,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  };
  
  onboarding.blockers.push(newBlocker);
  onboarding.healthScore = calculateHealthScore(onboarding);
  onboarding.updatedAt = new Date().toISOString();
  
  onboardingStore.set(onboardingId, onboarding);
  recordMetric('usage', 'onboarding_blocker_added', 1, { type: blocker.type, severity: blocker.severity });
  
  return newBlocker;
}

export async function resolveBlocker(
  onboardingId: string,
  blockerId: string
): Promise<boolean> {
  const onboarding = await getOnboarding(onboardingId);
  if (!onboarding) return false;
  
  const blocker = onboarding.blockers.find(b => b.id === blockerId);
  if (!blocker) return false;
  
  blocker.resolvedAt = new Date().toISOString();
  onboarding.healthScore = calculateHealthScore(onboarding);
  onboarding.updatedAt = new Date().toISOString();
  
  onboardingStore.set(onboardingId, onboarding);
  return true;
}

// ============================================================================
// Health Scoring
// ============================================================================

function calculateHealthScore(onboarding: CustomerOnboarding): number {
  let score = 100;
  
  // Deduct for active blockers
  const activeBlockers = onboarding.blockers.filter(b => !b.resolvedAt);
  for (const blocker of activeBlockers) {
    switch (blocker.severity) {
      case 'critical': score -= 30; break;
      case 'high': score -= 20; break;
      case 'medium': score -= 10; break;
      case 'low': score -= 5; break;
    }
  }
  
  // Deduct if behind schedule
  if (onboarding.targetCompletionDate) {
    const target = new Date(onboarding.targetCompletionDate);
    const now = new Date();
    const daysBehind = Math.floor((now.getTime() - target.getTime()) / (24 * 60 * 60 * 1000));
    
    if (daysBehind > 0 && onboarding.currentPhase !== 'fully_onboarded') {
      score -= Math.min(daysBehind * 2, 30);
    }
  }
  
  // Boost for good progress
  const requiredMilestones = ONBOARDING_MILESTONES.filter(m => 
    m.requiredForTier.includes(onboarding.contractTier)
  );
  const completedCount = onboarding.completedPhases.length;
  const progressRatio = completedCount / requiredMilestones.length;
  
  if (progressRatio > 0.7) score += 10;
  
  return Math.max(0, Math.min(100, score));
}

function calculateDaysToComplete(onboarding: CustomerOnboarding): number {
  if (!onboarding.actualCompletionDate) return -1;
  
  const start = new Date(onboarding.contractSignedAt);
  const end = new Date(onboarding.actualCompletionDate);
  
  return Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

// ============================================================================
// Batch Operations
// ============================================================================

export async function batchCreateOnboardings(
  customers: Array<{
    customerId: string;
    customerName: string;
    contractTier: CustomerOnboarding['contractTier'];
    contractedSeats: number;
  }>,
  csmId?: string,
  csmName?: string
): Promise<CustomerOnboarding[]> {
  const results: CustomerOnboarding[] = [];
  
  for (const customer of customers) {
    const onboarding = await createOnboarding({
      ...customer,
      csmId,
      csmName,
    });
    results.push(onboarding);
  }
  
  console.log(`[CustomerOnboarding] Batch created ${results.length} onboardings`);
  return results;
}

// ============================================================================
// Reporting
// ============================================================================

export interface OnboardingReport {
  generatedAt: string;
  totalCustomers: number;
  fullyOnboarded: number;
  inProgress: number;
  avgDaysToOnboard: number;
  avgHealthScore: number;
  byPhase: Record<OnboardingPhase, number>;
  byTier: Record<CustomerOnboarding['contractTier'], number>;
  activeBlockers: number;
  atRisk: CustomerOnboarding[];
}

export async function generateOnboardingReport(): Promise<OnboardingReport> {
  const onboardings = await listOnboardings();
  
  const fullyOnboarded = onboardings.filter(o => o.currentPhase === 'fully_onboarded');
  const inProgress = onboardings.filter(o => o.currentPhase !== 'fully_onboarded');
  
  // Calculate average days to onboard
  const completedDays = fullyOnboarded
    .map(calculateDaysToComplete)
    .filter(d => d >= 0);
  const avgDays = completedDays.length > 0
    ? completedDays.reduce((a, b) => a + b, 0) / completedDays.length
    : 0;
  
  // Average health score
  const avgHealth = onboardings.length > 0
    ? onboardings.reduce((sum, o) => sum + o.healthScore, 0) / onboardings.length
    : 100;
  
  // By phase
  const byPhase: Record<OnboardingPhase, number> = {} as Record<OnboardingPhase, number>;
  for (const milestone of ONBOARDING_MILESTONES) {
    byPhase[milestone.phase] = onboardings.filter(o => o.currentPhase === milestone.phase).length;
  }
  
  // By tier
  const byTier: Record<CustomerOnboarding['contractTier'], number> = {
    starter: 0,
    professional: 0,
    enterprise: 0,
  };
  for (const o of onboardings) {
    byTier[o.contractTier]++;
  }
  
  // Active blockers
  const activeBlockers = onboardings.reduce((sum, o) => 
    sum + o.blockers.filter(b => !b.resolvedAt).length, 0
  );
  
  // At risk (health < 60)
  const atRisk = onboardings.filter(o => o.healthScore < 60);
  
  return {
    generatedAt: new Date().toISOString(),
    totalCustomers: onboardings.length,
    fullyOnboarded: fullyOnboarded.length,
    inProgress: inProgress.length,
    avgDaysToOnboard: Math.round(avgDays),
    avgHealthScore: Math.round(avgHealth),
    byPhase,
    byTier,
    activeBlockers,
    atRisk,
  };
}


